import { Input, InputNumber, message, Modal, Select } from "antd";
import React, { useEffect, useMemo } from "react";
import {
  useCountryStruct,
  useCreateWarehouse,
  useGetWarehouseById,
  useUpdateWarehouse,
} from "../../api/admin";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const warehouseSchema = z.object({
  warehouseName: z
    .string()
    .min(1, { message: "Warehouse name is required" })
    .regex(/^[A-Za-z\s]+$/, {
      message: "Warehouse name must contain only letters",
    }),

  capacity: z.preprocess(
    (val) => {
      if (val === undefined || val === null || val === "") return undefined;
      return Number(val);
    },
    z.number().refine((val) => val !== 0, { message: "Capacity is required" })
  ),

  selectedRegionId: z.string().min(1, { message: "Region is required" }),
  selectedZoneId: z.string().min(1, { message: "Zone is required" }),
  selectedWoredaId: z.string().min(1, { message: "Woreda is required" }),
});

type WarehouseForm = z.infer<typeof warehouseSchema>;

type CreateWareHouseModalProps = {
  open: boolean;
  selectedId?: number;
  hideModal: () => void;
};

export type Woreda = {
  woredaId: number;
  woredaName: string;
};

export type Zone = {
  zoneId: number;
  zoneName: string;
  woredaDtos?: Woreda[];
};

export type Region = {
  regionId: number;
  regionName: string;
  zoneDtos?: Zone[];
};

const CreateWareHouseModal: React.FC<CreateWareHouseModalProps> = ({
  open,
  selectedId,
  hideModal,
}) => {
  const { data } = useCountryStruct();
  const { mutateAsync: createCenter, isPending } = useCreateWarehouse();
  const { mutateAsync: updateCenter } = useUpdateWarehouse();
  const { data: selectedWarehouse, refetch } = useGetWarehouseById(selectedId);

  const defaultValues = useMemo(
    () => ({
      warehouseName: "",
      capacity: undefined,
      selectedRegionId: "",
      selectedZoneId: "",
      selectedWoredaId: "",
    }),
    []
  );

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<WarehouseForm>({
    resolver: zodResolver(warehouseSchema) as any,
    defaultValues,
    mode: "onSubmit",
  });

  const regions: Region[] =
    data?.data?.data.regionDtoList.map((r: any) => ({
      regionId: r.regionId,
      regionName: r.regionName,
      zoneDtos: r.zoneDtos || [],
    })) || [];

  const selectedRegionId = watch("selectedRegionId");
  const selectedZoneId = watch("selectedZoneId");

  const zones: Zone[] =
    regions
      .find((r) => r.regionName === selectedRegionId)
      ?.zoneDtos?.map((z: any) => ({
        zoneId: z.zoneId,
        zoneName: z.zoneName,
        woredaDtos: z.woredaDtos || [],
      })) || [];

  const woredas: Woreda[] =
    zones
      .find((z) => z.zoneName === selectedZoneId)
      ?.woredaDtos?.map((w: any) => ({
        woredaId: w.woredaId,
        woredaName: w.woredaName,
      })) || [];

  useEffect(() => {
    if (!selectedId) {
      reset(defaultValues);
    } else {
      refetch();
    }
  }, [defaultValues, refetch, reset, selectedId]);

  useEffect(() => {
    if (selectedWarehouse?.data?.data) {
      const warehouse = selectedWarehouse.data.data;
      const [regionName, zoneName, woredaName] =
        warehouse.location?.split("/ ") || [];

      reset({
        warehouseName: warehouse.centerName,
        capacity: warehouse.capacity || undefined,
        selectedRegionId: regionName || "",
        selectedZoneId: zoneName || "",
        selectedWoredaId: woredaName || "",
      });
    }
  }, [reset, selectedWarehouse]);

  const onSubmit: SubmitHandler<WarehouseForm> = async (formData) => {
    try {
      const selectedRegion = regions.find(
        (r) => r.regionName === formData.selectedRegionId
      );
      if (!selectedRegion) {
        message.error("Invalid Region selected");
        return;
      }

      const selectedZone = selectedRegion.zoneDtos?.find(
        (z) => z.zoneName === formData.selectedZoneId
      );
      if (!selectedZone) {
        message.error("Invalid Zone selected");
        return;
      }

      const selectedWoreda = selectedZone.woredaDtos?.find(
        (w) => w.woredaName === formData.selectedWoredaId
      );
      if (!selectedWoreda) {
        message.error("Woreda is required");
        return;
      }

      if (selectedId) {
        await updateCenter({
          id: selectedId,
          warehouse: {
            centerName: formData.warehouseName,
            capacity: formData.capacity,
            woredaId: selectedWoreda.woredaId,
          },
        });
        message.success("Warehouse updated successfully.");
      } else {
        await createCenter({
          centerName: formData.warehouseName,
          capacity: formData.capacity,
          woredaId: selectedWoreda.woredaId,
        });
        message.success("Warehouse created successfully.");
      }

      hideModal();
      reset();
    } catch {
      message.error("Operation failed");
    }
  };

  return (
    <Modal
      title={selectedId ? "Edit Center" : "Create Center"}
      open={open}
      centered
      onCancel={hideModal}
      onOk={handleSubmit(onSubmit)}
      okText={isPending ? "Saving..." : "Ok"}
      okButtonProps={{ disabled: isPending }}
      afterClose={() => reset()}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Controller
          name="selectedRegionId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select region"
              options={regions.map((r) => ({
                label: r.regionName,
                value: r.regionName,
              }))}
              value={field.value || undefined}
              onChange={(val) => {
                field.onChange(val);
                reset({ ...watch(), selectedZoneId: "", selectedWoredaId: "" }); // clear dependent fields
              }}
              allowClear
            />
          )}
        />
        {errors.selectedRegionId && (
          <span style={{ color: "red" }}>
            {errors.selectedRegionId.message}
          </span>
        )}

        <Controller
          name="selectedZoneId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select zone"
              options={zones.map((z) => ({
                label: z.zoneName,
                value: z.zoneName,
              }))}
              value={field.value || undefined}
              onChange={(val) => {
                field.onChange(val);
                reset({ ...watch(), selectedWoredaId: "" });
              }}
              allowClear
            />
          )}
        />
        {errors.selectedZoneId && (
          <span style={{ color: "red" }}>{errors.selectedZoneId.message}</span>
        )}

        <Controller
          name="selectedWoredaId"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select woreda"
              options={woredas.map((w) => ({
                label: w.woredaName,
                value: w.woredaName,
              }))}
              value={field.value || undefined}
              onChange={(val) => field.onChange(val)}
              allowClear
            />
          )}
        />
        {errors.selectedWoredaId && (
          <span style={{ color: "red" }}>
            {errors.selectedWoredaId.message}
          </span>
        )}

        <Controller
          name="warehouseName"
          control={control}
          render={({ field }) => (
            <Input {...field} placeholder="Insert warehouse name" />
          )}
        />
        {errors.warehouseName && (
          <span style={{ color: "red" }}>{errors.warehouseName.message}</span>
        )}

        <Controller
          name="capacity"
          control={control}
          render={({ field }) => (
            <InputNumber
              {...field}
              value={field.value || undefined}
              style={{ width: "100%" }}
              placeholder="Insert warehouse capacity"
              addonBefore="Kg"
            />
          )}
        />

        {errors.capacity && (
          <span style={{ color: "red" }}>{errors.capacity.message}</span>
        )}
      </div>
    </Modal>
  );
};

export default CreateWareHouseModal;
